/*
 * Date and number wording for the Index Book (DESIGN-SPEC §1.12). Dates are
 * compared as local calendar days pinned to noon so a timestamp late at night
 * never slips into the neighbouring day when the offset changes.
 */

export const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const;

export const WEEKDAYS = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday',
] as const;

const NUMBER_WORDS = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];
const DAY_MS = 86_400_000;
const pad = (n: number) => String(n).padStart(2, '0');

export const compareInstants = (a: string, b: string): number => (Date.parse(a) || 0) - (Date.parse(b) || 0);

export const localDay = (d: Date, timeZone?: string): string => {
  if (!timeZone) return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const parts = new Intl.DateTimeFormat('en-US', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(d);
  const part = (type: string) => parts.find(p => p.type === type)!.value;
  return `${part('year')}-${part('month')}-${part('day')}`;
};

export const dayOf = (iso: string, timeZone?: string): string => (/^\d{4}-\d\d-\d\d$/.test(iso) ? iso : localDay(new Date(iso), timeZone));

/** A day written `YYYY-MM-DD`, whether or not the calendar has it. */
export const ISO_DAY = /^\d{4}-\d\d-\d\d$/;

/** Whether `iso` is a `YYYY-MM-DD` day that is on the calendar: `2026-02-28` is, `2026-02-30` and `2026-9-1` are not. */
export function isCalendarDay(iso: string): boolean {
  if (!ISO_DAY.test(iso)) return false;
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
}

export const atNoon = (iso: string): Date => new Date(`${dayOf(iso)}T12:00:00`);

export const daysBetween = (fromIso: string, now: Date): number =>
  Math.round((atNoon(localDay(now)).getTime() - atNoon(fromIso).getTime()) / DAY_MS);

export function weekStart(now: Date): string {
  const start = new Date(now);
  start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
  return localDay(start);
}

export const monthKey = (iso: string, timeZone?: string): string => dayOf(iso, timeZone).slice(0, 7);

export interface DateOptions {
  /** `'auto'` appends the year only when it differs from `now`'s year. */
  year?: boolean | 'auto';
  weekday?: boolean;
  now?: Date;
}

/** `23 September`, `23 September 2025`, `Wednesday 23 September 2026`. */
export function formatDate(iso: string | null | undefined, { year = 'auto', weekday = false, now = new Date() }: DateOptions = {}): string {
  if (!iso) return '';
  const d = atNoon(iso);
  const withYear = year === 'auto' ? d.getFullYear() !== now.getFullYear() : year;
  return `${weekday ? `${WEEKDAYS[d.getDay()]} ` : ''}${d.getDate()} ${MONTHS[d.getMonth()]}${withYear ? ` ${d.getFullYear()}` : ''}`;
}

/** `23 Sep`. */
export function formatShortDate(iso: string): string {
  const d = atNoon(iso);
  return `${d.getDate()} ${MONTHS[d.getMonth()]!.slice(0, 3)}`;
}

/** `today`, `yesterday`, the weekday name within a week, otherwise the date. */
export function formatRelativeDay(iso: string, now: Date = new Date()): string {
  const k = daysBetween(iso, now);
  if (k <= 0) return 'today';
  if (k === 1) return 'yesterday';
  if (k < 7) return WEEKDAYS[atNoon(iso).getDay()]!;
  return formatDate(iso, { now });
}

/** 24-hour local clock time, e.g. `14:02`, for save states. */
export function formatClock(date: Date): string {
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** `September 2026`. */
export function formatMonth(key: string): string {
  const [y, m] = key.split('-').map(Number);
  return `${MONTHS[(m ?? 1) - 1]} ${y}`;
}

export const formatNumber = (n: number): string => n.toLocaleString('en-GB');

/** `1 section`, `3 sections`, `1,975 sections`. */
export const plural = (n: number, one: string, many = `${one}s`): string => `${formatNumber(n)} ${n === 1 ? one : many}`;

/** Counts 0–10 as prose words (`no`, `one` … `ten`), larger counts as numerals. */
export const numberWord = (n: number): string => NUMBER_WORDS[n] ?? formatNumber(n);

export const capitalise = (s: string): string => (s ? s[0]!.toUpperCase() + s.slice(1) : s);

/** Whole minutes at 230 words a minute, never less than one. */
export const readingMinutes = (words: number): number => Math.max(1, Math.round(words / 230));

/** `about 7 minutes`, `about 2 hours`. */
export function formatReadingTime(words: number): string {
  const minutes = readingMinutes(words);
  if (minutes < 60) return `about ${plural(minutes, 'minute')}`;
  return `about ${plural(Math.round(minutes / 60), 'hour')}`;
}
